import type { ImageCDNProvider } from '@packages/common';
import { getCloudinaryImageSrc } from '@packages/common';

import {
  BreakpointFlag,
  ResponsiveImageConfig,
  ResponsiveImageConfigMap,
  ResponsiveImageConfigOverride,
  SchemalessImageMapping,
} from './ResponsiveImageTypes';

export const hasBreakpoint = (flag: BreakpointFlag, flags: BreakpointFlag): boolean => {
  return flags === BreakpointFlag.All || (flags & flag) === flag;
};

export const toSrcSet = (
  path: string,
  extension: string,
  deliveryFormat: string,
  query = '',
  configs: ResponsiveImageConfig[],
  cdnProvider?: ImageCDNProvider,
): string => {
  // TODO: add optional quality param after '.${extension}'
  return configs
    .map(({ filenameKey, realWidth, visibleFrom }) => {
      const width = `${realWidth ?? visibleFrom}w`;
      if (path !== '' && !cdnProvider) {
        const separator = '-';
        return `${path}${separator}${filenameKey}.${extension}${query ? `?${query}` : ''} ${width}`;
      }
      // in case we don't have a path, we assume the image-key is the full path
      const src = cdnProvider === 'cloudinary' ? getCloudinaryImageSrc(filenameKey, deliveryFormat) : filenameKey;

      return `${src} ${width}`;
    })
    .join(', ');
};

export const toImgSizes = (...configs: ResponsiveImageConfig[]): string => {
  return configs
    .map(({ realWidth, visibleFrom }) => `(min-width: ${visibleFrom}px) ${realWidth ?? visibleFrom}px`)
    .join(', ');
};

export const createConfigsFromFlags = (
  breakpoints: BreakpointFlag,
  configMap: ResponsiveImageConfigMap,
): ResponsiveImageConfig[] => {
  const config: ResponsiveImageConfig[] = [];

  Object.keys(configMap).forEach((breakpoint) => {
    if (hasBreakpoint(parseInt(breakpoint, 10), breakpoints)) {
      config.push(configMap[Number(breakpoint)]);
    }
  });

  return config;
};

export const toConfigPairs = (configs: ResponsiveImageConfig[]): [ResponsiveImageConfig, ResponsiveImageConfig?][] => {
  const pairBreakpoints: [ResponsiveImageConfig, ResponsiveImageConfig?][] = [];

  configs.forEach((config, idx) => {
    if (idx < configs.length - 1) {
      const next = configs[idx + 1];
      pairBreakpoints.push([config, next]);
    } else {
      pairBreakpoints.push([config]);
    }
  });

  return pairBreakpoints;
};

/**
 * @description because {...sourceConfig, ...configOverride} would just do a shallow merge
 * @param sourceConfig
 * @param configOverride
 */
export const mergeWithConfig = (
  sourceConfig: ResponsiveImageConfigMap,
  configOverride?: ResponsiveImageConfigOverride,
): ResponsiveImageConfigMap => {
  if (!configOverride) {
    return sourceConfig;
  }

  const configMap: ResponsiveImageConfigMap = { ...sourceConfig };

  Object.keys(sourceConfig).forEach((breakpoint) => {
    const originalConfig: ResponsiveImageConfig = sourceConfig[Number(breakpoint)];
    const overrideConfig: Partial<ResponsiveImageConfig> = configOverride[Number(breakpoint)];

    if (overrideConfig) {
      configMap[Number(breakpoint)] = { ...originalConfig, ...overrideConfig };
    }
  });

  return configMap;
};

function imageMappingToTuples(mapping: SchemalessImageMapping): [BreakpointFlag, string | null][] {
  return Object.entries(mapping)
    .sort(([breakpointA], [breakpointB]) => parseInt(breakpointA, 10) - parseInt(breakpointB, 10))
    .map(([breakpoint, url]) => [parseInt(breakpoint, 10), url]);
}

/**
 * Given a SchemalessImageMapping this will return a (combined) BreakpointFlag.
 * Always uses XS for smallest Breakpoint. Filters out invalid data
 * @example (pseudo-code)
 * imageMappingToTuples({ [XS]: 'some/url', [MD]: 'some/url' }) -> XS | MD
 * imageMappingToTuples({ [MD]: 'some/url', [XL]: 'some/url', [XXL]: 'some/url' }) -> XS | XL | XXL
 * imageMappingToTuples({ [MD]: 'some/url', [XL]: null, [XXL]: 'some/url' }) -> XS | XXL
 */
export function imageMappingToBreakpointFlag(mapping: SchemalessImageMapping): BreakpointFlag {
  return imageMappingToTuples(mapping)
    .filter(([_, url]) => url) // filter out invalid data
    .reduce((acc, [breakpoint], idx) => {
      // mappingTuples is sorted, so the first breakpoint is always the lowest
      // breakpoint. And we want to start from XS
      return idx === 0 ? acc : acc | breakpoint;
    }, BreakpointFlag.XS);
}

/**
 * Given a SchemalessImageMapping this will return a config override. Always
 * uses XS for smallest Breakpoint. Filters out invalid/undefined data
 */
export function imageMappingToConfigOverride(mapping: SchemalessImageMapping): ResponsiveImageConfigOverride {
  const configOverride: ResponsiveImageConfigOverride = {};

  imageMappingToTuples(mapping).forEach((image, idx) => {
    const [breakpoint, src] = image;

    if (src !== null) {
      if (idx === 0) {
        configOverride[BreakpointFlag.XS] = { filenameKey: src };
      } else {
        configOverride[breakpoint] = {
          filenameKey: src,
        };
      }
    }
  });

  return configOverride;
}
